import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, TrendingUp } from 'lucide-react';

const PricingPlanManager = ({ courseId, onClose }) => {
  const [plans, setPlans] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    id: null,
    course_id: courseId,
    plan_name: '',
    duration_in_days: '',
    price: '',
    is_active: true,
    is_best_value: false,
    display_order: 1
  });

  useEffect(() => {
    if (courseId) {
      fetchPlans();
    }
  }, [courseId]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`/pricing-plans/${courseId}`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (form.id) {
        await axios.put(`/admin/pricing-plans/${form.id}`, form, config);
        toast.success('Pricing plan updated');
      } else {
        await axios.post('/admin/pricing-plans', form, config);
        toast.success('Pricing plan created');
      }

      setShowDialog(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error('Failed to save pricing plan:', error);
      toast.error('Failed to save pricing plan');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing plan?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/admin/pricing-plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete pricing plan');
    }
  };

  const resetForm = () => {
    setForm({
      id: null,
      course_id: courseId,
      plan_name: '',
      duration_in_days: '',
      price: '',
      is_active: true,
      is_best_value: false,
      display_order: 1
    });
  };

  const openEditDialog = (plan) => {
    setForm(plan);
    setShowDialog(true);
  };

  const calculatePerMonthCost = (price, days) => {
    const months = days / 30;
    return (price / months).toFixed(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Pricing Plans</h3>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} size="sm">
          <Plus className="w-4 h-4 mr-2" /> Add Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <Card key={plan.id} className={`relative ${plan.is_best_value ? 'border-2 border-blue-500' : ''}`}>
            {plan.is_best_value && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Star className="w-3 h-3" /> Best Value
                </span>
              </div>
            )}
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{plan.plan_name}</CardTitle>
              <div className="text-3xl font-bold text-blue-600">
                ₹{plan.price}
              </div>
              <p className="text-sm text-gray-500">
                ₹{calculatePerMonthCost(plan.price, plan.duration_in_days)}/month
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold">{plan.duration_in_days} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={plan.is_active ? 'text-green-600' : 'text-red-600'}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(plan)} className="flex-1">
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(plan.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {plans.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No pricing plans yet. Create your first plan!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Pricing Plan' : 'Create Pricing Plan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Plan Name</label>
              <Input
                value={form.plan_name}
                onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                placeholder="e.g., 2 Months, 3 Months"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Duration (days)</label>
              <Input
                type="number"
                value={form.duration_in_days}
                onChange={(e) => setForm({ ...form, duration_in_days: parseInt(e.target.value) })}
                placeholder="60, 90, 180"
                min="1"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {form.duration_in_days ? `≈ ${(form.duration_in_days / 30).toFixed(1)} months` : ''}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Price (₹)</label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })}
                placeholder="400"
                min="0"
                step="0.01"
                required
              />
              {form.price && form.duration_in_days && (
                <p className="text-xs text-blue-600 mt-1">
                  ≈ ₹{calculatePerMonthCost(form.price, form.duration_in_days)}/month
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Display Order</label>
              <Input
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) })}
                min="1"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Active</label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Best Value Badge
              </label>
              <Switch
                checked={form.is_best_value}
                onCheckedChange={(checked) => setForm({ ...form, is_best_value: checked })}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowDialog(false); resetForm(); }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Plan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingPlanManager;
